import { useState, useEffect, useMemo, useCallback, useRef, startTransition } from 'react';
import { T, amountStyle } from '../theme';
import { loadFile, saveFile } from '../utils/firestoreStorage';
import { UNCLASSIFIED_KEYS, buildTransferPairIds, classifyTransaction } from '../utils/autoClassify';
import { TriangleAlert, RefreshCw, TrendingUp, TrendingDown, Minus, ChevronDown, ArrowUpDown, Layers, Link2, GitMerge, Camera, Edit, Share2, Trash2, HelpCircle, ArrowLeft } from 'lucide-react';
import DateRangePicker, { getPresetRange } from './DateRangePicker';
import { formatAmount } from '../utils/formatters';
import { CATEGORIES, INCOME_CATEGORIES, BIZ_CLASS, CLASS_SELECT_KEYS, CATEGORY_KEY_BY_LABEL, INCOME_CAT_KEY_BY_LABEL, EXPENSE_REFUND_CLASS_KEYS } from '../utils/constants';
import { categoryLabel, normalizeCategory, makeKey } from '../utils/categoryHelpers';
import EditModal from './EditModal';
import ReconcileModal from './ReconcileModal';
import BSVHelpModal from './BSVHelpModal';
import { FinanceViewNav, FinanceViewTopBar } from './NavShared';
import { useWindowWidth } from '../hooks/useWindowWidth';

function lsSettingsKey(uid) { return uid ? `biz_summary_settings_${uid}_v1` : 'biz_summary_settings_v1'; }
// Rewrite legacy persisted classification keys so existing users don't lose visibility
// after the personal → personal_expense rename.
function coerceLegacyKey(k) { return k === 'personal' ? 'personal_expense' : k; }
function loadSettings(uid) {
  try {
    const raw = JSON.parse(localStorage.getItem(lsSettingsKey(uid))) ?? {};
    if (Array.isArray(raw.filterSet)) raw.filterSet = raw.filterSet.map(coerceLegacyKey);
    if (raw.filter) raw.filter = coerceLegacyKey(raw.filter);
    return raw;
  }
  catch { return {}; }
}


const REPORT_ICONS = { credit_card: '💳', bank: '🏦', expense: '🧾' };

function ClassBadge({ cls }) {
  const c = BIZ_CLASS[cls] || BIZ_CLASS.unclassified_debit;
  return (
    <span style={{
      fontSize: 10, fontWeight: 700, padding: '2px 7px', borderRadius: 10,
      background: c.bg, color: c.color, whiteSpace: 'nowrap',
    }}>
      {c.label}
    </span>
  );
}

function ClassSelect({ value, onChange }) {
  // Normalise legacy value so the select doesn't show a blank option
  const effectiveValue = value === 'unclassified' ? 'unclassified_debit' : (value || 'unclassified_debit');
  return (
    <select
      value={effectiveValue}
      onChange={e => onChange(e.target.value)}
      onClick={e => e.stopPropagation()}
      style={{
        fontSize: 11, padding: '3px 6px', borderRadius: 6,
        border: '1.5px solid #D1D5DB', cursor: 'pointer',
        background: '#fff', fontWeight: 600,
      }}
    >
      {CLASS_SELECT_KEYS.map(k => (
        <option key={k} value={k}>{BIZ_CLASS[k].label}</option>
      ))}
    </select>
  );
}

// ─── Income categories ────────────────────────────────────────────────────────

function CategorySelect({ value, onChange, categories, isIncome }) {
  const activeColor = isIncome ? '#15803d' : T.tealDeep;
  const activeBg = isIncome ? '#f0fdf4' : T.tealPale;
  const normalizedValue = normalizeCategory(value) || '';
  return (
    <select
      value={normalizedValue}
      onChange={e => onChange(e.target.value)}
      onClick={e => e.stopPropagation()}
      style={{
        fontSize: 11, padding: '3px 6px', borderRadius: 6,
        border: `1.5px solid ${value ? activeColor : '#D1D5DB'}`, cursor: 'pointer',
        background: value ? activeBg : '#fff', fontWeight: 600, color: value ? activeColor : '#374151',
      }}
    >
      <option value="">— select category —</option>
      {categories.map(c => {
        const key = CATEGORY_KEY_BY_LABEL[c] ?? INCOME_CAT_KEY_BY_LABEL[c] ?? makeKey(c);
        return <option key={key} value={key}>{c}</option>;
      })}
    </select>
  );
}

// Classification → which category picker to show
const INCOME_CLS = new Set(['business_income', 'unclassified_credit', 'personal_income']);
const EXPENSE_CLS = new Set(['business_expense', 'unclassified_debit', 'personal_expense', 'business_expense_refund', 'personal_expense_refund']);


const SORT_OPTIONS = [
  { value: 'date_asc', label: 'Date ↑' },
  { value: 'date_desc', label: 'Date ↓' },
  { value: 'amount_desc', label: 'Amount ↓' },
  { value: 'amount_asc', label: 'Amount ↑' },
  { value: 'vendor_asc', label: 'Name A→Z' },
  { value: 'vendor_desc', label: 'Name Z→A' },
];

const GROUP_OPTIONS = [
  { value: 'none', label: 'No grouping' },
  { value: 'month', label: 'By month' },
  { value: 'classification', label: 'By classification' },
  { value: 'category', label: 'By category' },
  { value: 'source', label: 'By source type' },
  { value: 'expense_report', label: 'By Expense Report' },
  { value: 'tx_account', label: 'By Transaction Account' },
];

function txSortKey(tx) {
  return parseFloat(tx.convertedTotal ?? tx.total ?? tx.amount ?? 0);
}

function sortTransactions(txs, sortBy) {
  return [...txs].sort((a, b) => {
    switch (sortBy) {
      case 'date_asc': return (a.date || '') < (b.date || '') ? -1 : (a.date || '') > (b.date || '') ? 1 : 0;
      case 'date_desc': return (a.date || '') > (b.date || '') ? -1 : (a.date || '') < (b.date || '') ? 1 : 0;
      case 'amount_desc': return txSortKey(b) - txSortKey(a);
      case 'amount_asc': return txSortKey(a) - txSortKey(b);
      case 'vendor_asc': return (a.vendor || '').localeCompare(b.vendor || '');
      case 'vendor_desc': return (b.vendor || '').localeCompare(a.vendor || '');
      default: return 0;
    }
  });
}

function getGroupKey(tx, groupBy, ctx = {}) {
  switch (groupBy) {
    case 'month': return tx.date ? tx.date.slice(0, 7) : 'No date';
    case 'classification': return tx.businessClassification || 'unclassified';
    case 'category': return categoryLabel(normalizeCategory(tx.category)) || 'Uncategorized';
    case 'source': return tx._reportType === 'expense' ? 'expense' : 'transaction';
    case 'expense_report': {
      if (tx._reportType === 'expense') return tx._reportName || 'Unknown';
      // Linked merged row (CC/bank base) — find the expense source
      const expSrc = tx._sourceReports?.find(s => s.type === 'expense');
      return expSrc ? expSrc.name : null;
    }
    case 'tx_account': {
      if (tx._reportType !== 'expense') {
        return ctx.fileToAccount?.get(tx._reportName) ?? tx._reportName ?? 'Unknown';
      }
      // Linked merged row (expense base, rare) — find the CC/bank source
      const txSrc = tx._sourceReports?.find(s => s.type !== 'expense');
      return txSrc ? (ctx.fileToAccount?.get(txSrc.name) ?? txSrc.name) : null;
    }
    default: return null;
  }
}

function groupLabel(key, groupBy) {
  if (groupBy === 'month' && key && key.length === 7) {
    const [y, m] = key.split('-');
    return new Date(Number(y), Number(m) - 1, 1).toLocaleDateString('en-US', { month: 'long', year: 'numeric' });
  }
  if (groupBy === 'classification') return BIZ_CLASS[key]?.label ?? key;
  if (groupBy === 'source') return key === 'expense' ? 'Expense Reports' : 'Transaction Accounts';

  return key;
}

function groupNetTotals(items) {
  let credits = 0, debits = 0;
  for (const t of items) {
    const amt = parseFloat(t.convertedTotal ?? t.total ?? t.amount ?? 0);
    const isCredit = t.transactionDirection === 'credit' || t.transactionType === 'credit';
    if (isCredit) credits += amt; else debits += amt;
  }
  return { credits, debits, net: credits - debits };
}

// ─── Main component ───────────────────────────────────────────────────────────

export default function BusinessSummaryView({ uid, savedFiles,
  defaultCurrency, onGoToDashboard, categories = CATEGORIES, incomeCategories,
  addCategory = null, addIncomeCategory = null, canAiReconcile = false,
  allLoaded = {}, setAllLoaded = () => { }, onGoToFinancial, onGoToTax,
  onGoToExpenses, onGoToAdd, onGoToCapture, onImportCreditCard, onImportBank,
  onShareClick, onGoToTransactionAccount,
  topBarProps,
  initialFilters = null,
  onFilePatch,
  initialShowReconcile = false,
}) {

  const windowWidth = useWindowWidth();

  // Capture initialFilters at mount so the persist effect can skip when it's active
  const initialFiltersRef = useRef(initialFilters);

  // Deselected names — not persisted, defaults to all selected on every mount.
  const [deselected, setDeselected] = useState(new Set());

  // Derive selected list.
  // When initialFilters.fileSelection is set, override deselected entirely —
  // this is computed synchronously so it works from the very first render.
  const selectedFiles = useMemo(() => {
    const sel = initialFiltersRef.current?.fileSelection;
    if (sel === 'all') return savedFiles.map(f => f.name);
    if (sel === 'none') return [];
    if (sel === 'expense') return savedFiles.filter(f => f.reportType === 'expense' || !f.reportType).map(f => f.name);
    if (sel === 'transaction') return savedFiles.filter(f => f.reportType === 'credit_card' || f.reportType === 'bank').map(f => f.name);
    if (Array.isArray(sel)) { const s = new Set(sel); return savedFiles.filter(f => s.has(f.name)).map(f => f.name); }
    return savedFiles.filter(f => !deselected.has(f.name)).map(f => f.name);
  }, [savedFiles, deselected]);

  // Persisted settings (sort, group, filter, date range, panel open/closed)
  const [filterSet, setFilterSet] = useState(() => {
    if (initialFilters?.filterSet) return new Set(initialFilters.filterSet);
    const s = loadSettings(uid);
    if (Array.isArray(s.filterSet)) return new Set(s.filterSet);
    if (s.filter && s.filter !== 'all') return new Set([s.filter]);
    return new Set(CLASS_SELECT_KEYS);
  });
  const [filterAccounts, setFilterAccounts] = useState(() => {
    if (initialFilters !== null) {
      // Populate with every account key so all accounts are visible
      return new Set(savedFiles.map(f =>
        (f.reportType === 'credit_card' || f.reportType === 'bank')
          ? (f.accountName || f.name)
          : f.name
      ));
    }
    const s = loadSettings(uid);
    if (Array.isArray(s.filterAccounts)) return new Set(s.filterAccounts);
    // migrate old single-value string
    if (s.filterAccount && s.filterAccount !== 'all') return new Set([s.filterAccount]);
    return new Set();
  });
  const [sortBy, setSortBy] = useState(() => loadSettings(uid).sortBy ?? 'date_asc');
  const [groupBy, setGroupBy] = useState(() => {
    if (initialFilters?.groupBy != null) return initialFilters.groupBy;
    return loadSettings(uid).groupBy ?? 'none';
  });
  const [dateFrom, setDateFrom] = useState(() => {
    if (initialFilters?.dateFrom != null) return initialFilters.dateFrom;
    return loadSettings(uid).dateFrom ?? getPresetRange('this_month').from;
  });
  const [dateTo, setDateTo] = useState(() => {
    if (initialFilters?.dateTo != null) return initialFilters.dateTo;
    return loadSettings(uid).dateTo ?? getPresetRange('this_month').to;
  });
  const [reportsOpen, setReportsOpen] = useState(() => initialFilters !== null ? false : (loadSettings(uid).reportsOpen ?? false));
  const [allCollapse, setAllCollapse] = useState(() => initialFilters !== null ? true : (loadSettings(uid).allCollapse ?? false));
  const [controlsCollapsed, setControlsCollapsed] = useState(() => initialFilters !== null ? false : (loadSettings(uid).controlsCollapsed ?? false));
  const [classificationsOpen, setClassificationsOpen] = useState(() => initialFilters !== null ? false : (loadSettings(uid).classificationsOpen ?? false));
  const [topHeight, setTopHeight] = useState(() => loadSettings(uid).topSectionHeight ?? 320);
  const isDragging = useRef(false);
  const dragStartY = useRef(0);
  const dragStartHeight = useRef(0);
  useEffect(() => {
    // Don't clobber user's saved settings when opened with dashboard-driven initialFilters
    if (initialFiltersRef.current !== null) return;
    localStorage.setItem(lsSettingsKey(uid), JSON.stringify(
      { filterSet: [...filterSet], filterAccounts: [...filterAccounts], sortBy, groupBy, dateFrom, dateTo, reportsOpen, allCollapse, controlsCollapsed, classificationsOpen, topSectionHeight: topHeight }
    ));
  }, [uid, filterSet, filterAccounts, sortBy, groupBy, dateFrom, dateTo, reportsOpen, allCollapse, controlsCollapsed, classificationsOpen, topHeight]);

  const [loading, setLoading] = useState(false);
  const [collapsedGroups, setCollapsedGroups] = useState(new Set());
  const [expandedTx, setExpandedTx] = useState(null);
  const [saving, setSaving] = useState({});
  const [pendingEdits, setPendingEdits] = useState({});
  const [bulkOffer, setBulkOffer] = useState(null);
  const [bulkSaving, setBulkSaving] = useState(false);
  const [linkMode, setLinkMode] = useState(false);
  const [linkSelected, setLinkSelected] = useState([]);
  const [linkSaving, setLinkSaving] = useState(false);
  const [editingTx, setEditingTx] = useState(null);
  const [showReconcile, setShowReconcile] = useState(initialShowReconcile);
  const [showHelp, setShowHelp] = useState(false);

  // Group saved files by type
  const creditCardFiles = savedFiles.filter(f => f.reportType === 'credit_card');
  const bankFiles = savedFiles.filter(f => f.reportType === 'bank');
  const expenseFiles = savedFiles.filter(f => f.reportType === 'expense' || !f.reportType);
  const allFilesSorted = [...creditCardFiles, ...bankFiles, ...expenseFiles];

  // Group transaction files by accountName for display in the reports section
  const accountGroups = useMemo(() => {
    const groups = new Map(); // accountKey → { accountName, reportType, files, lastImport }
    for (const f of [...creditCardFiles, ...bankFiles]) {
      const key = f.accountName || f.name;
      if (!groups.has(key)) {
        groups.set(key, { accountName: key, reportType: f.reportType, files: [], lastImport: null });
      }
      const g = groups.get(key);
      g.files.push(f);
      if (f.updatedAt && (!g.lastImport || f.updatedAt > g.lastImport)) g.lastImport = f.updatedAt;
    }
    return Array.from(groups.values());
  }, [creditCardFiles, bankFiles]); // eslint-disable-line react-hooks/exhaustive-deps

  // file name → account name for tx files (used by filters)
  const fileToAccount = useMemo(() => {
    const map = new Map();
    for (const f of [...creditCardFiles, ...bankFiles]) {
      map.set(f.name, f.accountName || f.name);
    }
    return map;
  }, [creditCardFiles, bankFiles]);

  // Stale accounts: last import > 30 days ago
  const staleAccounts = useMemo(() => {
    const cutoff = new Date(Date.now() - 30 * 24 * 60 * 60 * 1000);
    return accountGroups.filter(g => g.lastImport && g.lastImport < cutoff);
  }, [accountGroups]);

  // Load selected files from Firestore
  // Full re-fetch of all selected files — used by the refresh button
  const loadSelected = useCallback(async () => {
    if (!uid || !selectedFiles?.length) { setAllLoaded({}); return; }
    setLoading(true);
    try {
      const loaded = {};
      await Promise.all(selectedFiles.map(async (name) => {
        const fileInfo = savedFiles.find(f => f.name === name);
        const file = await loadFile(uid, name, fileInfo?.reportType);
        if (file) loaded[name] = file.data;
      }));
      setAllLoaded(loaded);
    } finally {
      setLoading(false);
    }
  }, [uid, selectedFiles, savedFiles]);

  // Lazy-load only files not already cached — runs when selection changes
  const loadMissing = useCallback(async () => {
    if (!uid || !selectedFiles?.length || !savedFiles.length) return;
    const missing = selectedFiles.filter(name => !(name in allLoaded));
    if (!missing.length) return;
    setLoading(true);
    try {
      const updates = {};
      await Promise.all(missing.map(async (name) => {
        const fileInfo = savedFiles.find(f => f.name === name);
        const file = await loadFile(uid, name, fileInfo?.reportType);
        if (file) updates[name] = file.data;
      }));
      setAllLoaded(prev => ({ ...prev, ...updates }));
    } finally {
      setLoading(false);
    }
  }, [uid, selectedFiles, savedFiles, allLoaded]);

  useEffect(() => { loadMissing(); }, [loadMissing]);

  // All expense-report items (from ALL expense files, not just selected ones) for auto-classification
  const allExpenseReportItems = useMemo(() => {
    return expenseFiles.flatMap(f => f.data ?? []);
  }, [expenseFiles]);

  // Build flat list of all transactions from selected files only
  const allTransactions = useMemo(() => {
    const txs = [];
    const selected = new Set(selectedFiles ?? []);
    for (const [reportName, data] of Object.entries(allLoaded)) {
      if (!selected.has(reportName)) continue;
      const fileInfo = savedFiles.find(f => f.name === reportName);
      const rType = fileInfo?.reportType ?? 'expense';
      for (const tx of (data ?? [])) {
        txs.push({ ...tx, _reportName: reportName, _reportType: rType });
      }
    }
    return txs;
  }, [allLoaded, savedFiles, selectedFiles]);

  const transferPairIds = useMemo(() => buildTransferPairIds(allTransactions), [allTransactions]);

  const classifiedTransactions = useMemo(() => allTransactions.map(tx =>
    classifyTransaction(tx, { transferIds: transferPairIds, allExpenseItems: allExpenseReportItems })
  ), [allTransactions, transferPairIds, allExpenseReportItems]);

  // Auto-persist auto-classifications back to Firestore for any transaction stored with null
  // businessClassification. Only fires when classifiedTransactions changes (new files loaded).
  // After saving, allLoaded has non-null values so this becomes a no-op on the next render.
  useEffect(() => {
    if (!uid) return;
    const rawIndex = {};
    for (const [name, data] of Object.entries(allLoaded)) {
      rawIndex[name] = new Map((data ?? []).map(r => [r.id, r]));
    }
    const toSave = {};
    for (const tx of classifiedTransactions) {
      // Only persist definitive classifications — saving an unclassified value would
      // leave allLoaded with a value still in UNCLASSIFIED_KEYS, causing an infinite loop.
      if (!tx.businessClassification || UNCLASSIFIED_KEYS.has(tx.businessClassification)) continue;
      const raw = rawIndex[tx._reportName]?.get(tx.id);
      if (!raw || (raw.businessClassification && !UNCLASSIFIED_KEYS.has(raw.businessClassification))) continue;
      if (!toSave[tx._reportName]) toSave[tx._reportName] = new Map();
      toSave[tx._reportName].set(tx.id, tx.businessClassification);
    }
    for (const [reportName, clsMap] of Object.entries(toSave)) {
      const fileData = allLoaded[reportName];
      const updated = fileData.map(tx =>
        clsMap.has(tx.id) ? { ...tx, businessClassification: clsMap.get(tx.id) } : tx
      );
      const fileInfo = savedFiles.find(f => f.name === reportName);
      saveFile(uid, reportName, updated, { imageStorage: false, reportType: fileInfo?.reportType, local: fileInfo?.local });
      setAllLoaded(prev => ({ ...prev, [reportName]: updated }));
    }
  // allLoaded omitted from deps intentionally: including it creates an infinite loop since
  // saving updates allLoaded → classifiedTransactions recomputes → effect fires again.
  // After saving, stored values are non-null so toSave is empty and the effect is a no-op.
  }, [classifiedTransactions, uid, savedFiles]); // eslint-disable-line react-hooks/exhaustive-deps

  // Persist a classification change; handles merged (multi-source) rows
  const updateClassification = useCallback(async (tx, newClass) => {
    if (!uid) return;
    setBulkOffer(null);
    setSaving(prev => ({ ...prev, [tx.id]: true }));
    try {
      const sources = tx._sourceReports ?? [{ name: tx._reportName, type: tx._reportType, id: tx.id }];
      const sourceIdSet = new Set(sources.map(s => s.id));

      // Save to every source report this display row represents
      for (const { name: reportName, type: reportType, id: sourceId } of sources) {
        const reportData = allLoaded[reportName];
        if (!reportData) continue;
        const updated = reportData.map(r =>
          r.id === sourceId ? { ...r, businessClassification: newClass } : r
        );
        const fileInfo = savedFiles.find(f => f.name === reportName);
        await saveFile(uid, reportName, updated, {
          imageStorage: false,
          reportType: fileInfo?.reportType ?? reportType,
        });
        setAllLoaded(prev => ({ ...prev, [reportName]: updated }));
      }

      // Look for same-vendor peers in the primary report that weren't just updated
      const primaryName = sources[0].name;
      const primaryData = allLoaded[primaryName] ?? [];
      const vendor = (tx.vendor || '').trim().toLowerCase();
      const peers = primaryData.filter(r =>
        !sourceIdSet.has(r.id) &&
        (r.vendor || '').trim().toLowerCase() === vendor &&
        (r.businessClassification || 'unclassified') !== newClass
      );
      if (peers.length > 0) {
        setBulkOffer({
          vendor: tx.vendor,
          reportName: primaryName,
          newClass,
          // newCategory intentionally undefined — AI path, leave category alone
          matchIds: peers.map(r => r.id),
        });
      }
    } finally {
      setSaving(prev => ({ ...prev, [tx.id]: false }));
    }
  }, [uid, allLoaded, savedFiles]);

  // Save all pending edits (classification, category) in one write per source file
  const saveItemChanges = useCallback(async (tx, { cls, cat }) => {
    if (!uid) return;
    setSaving(prev => ({ ...prev, [tx.id]: true }));
    try {
      const sources = tx._sourceReports ?? [{ name: tx._reportName, type: tx._reportType, id: tx.id }];
      const sourceIdSet = new Set(sources.map(s => s.id));

      for (const { name: reportName, type: reportType, id: sourceId } of sources) {
        const reportData = allLoaded[reportName];
        if (!reportData) continue;
        const updated = reportData.map(r => {
          if (r.id !== sourceId) return r;
          const next = { ...r, businessClassification: cls, category: cat || null };
          // Strip income-only field when classification is no longer income
          if (!INCOME_CLS.has(cls) && next.incomeTaxType) delete next.incomeTaxType;
          return next;
        });
        const fileInfo = savedFiles.find(f => f.name === reportName);
        await saveFile(uid, reportName, updated, {
          imageStorage: false,
          reportType: fileInfo?.reportType ?? reportType,
          accountName: fileInfo?.accountName,
        });
        setAllLoaded(prev => ({ ...prev, [reportName]: updated }));
      }

      // Offer to bulk-apply the same classification to same-vendor peers
      const storedCls = tx.businessClassification || 'unclassified_debit';
      if (cls !== storedCls) {
        const primaryName = sources[0].name;
        const primaryData = allLoaded[primaryName] ?? [];
        const vendor = (tx.vendor || '').trim().toLowerCase();
        const peers = primaryData.filter(r =>
          !sourceIdSet.has(r.id) &&
          (r.vendor || '').trim().toLowerCase() === vendor &&
          (r.businessClassification || 'unclassified') !== cls
        );
        if (peers.length > 0) {
          setBulkOffer({ vendor: tx.vendor, reportName: primaryName, newClass: cls, newCategory: cat || null, matchIds: peers.map(r => r.id) });
        }
      }

      setPendingEdits(prev => { const n = { ...prev }; delete n[tx.id]; return n; });
    } finally {
      setSaving(prev => ({ ...prev, [tx.id]: false }));
    }
  }, [uid, allLoaded, savedFiles]);

  // Apply the bulk offer — update all matching IDs in a single save
  const applyBulkOffer = useCallback(async () => {
    if (!uid || !bulkOffer) return;
    setBulkSaving(true);
    try {
      const reportData = allLoaded[bulkOffer.reportName];
      if (!reportData) return;
      const idSet = new Set(bulkOffer.matchIds);
      const updated = reportData.map(r => {
        if (!idSet.has(r.id)) return r;
        const next = { ...r, businessClassification: bulkOffer.newClass };
        // newCategory: undefined means AI/classification-only offer — leave category alone
        if (bulkOffer.newCategory !== undefined) next.category = bulkOffer.newCategory;
        if (!INCOME_CLS.has(bulkOffer.newClass) && next.incomeTaxType) delete next.incomeTaxType;
        return next;
      });
      const fileInfo = savedFiles.find(f => f.name === bulkOffer.reportName);
      await saveFile(uid, bulkOffer.reportName, updated, {
        imageStorage: false,
        reportType: fileInfo?.reportType,
      });
      setAllLoaded(prev => ({ ...prev, [bulkOffer.reportName]: updated }));
      setBulkOffer(null);
    } finally {
      setBulkSaving(false);
    }
  }, [uid, bulkOffer, allLoaded, savedFiles]);

  // Remove pairedWithId from all source reports of a linked display row
  const unlinkPair = useCallback(async (tx) => {
    if (!uid || !tx._sourceReports) return;
    setSaving(prev => ({ ...prev, [tx.id]: true }));
    try {
      for (const { name: reportName, type: reportType, id: sourceId } of tx._sourceReports) {
        const reportData = allLoaded[reportName];
        if (!reportData) continue;
        const updated = reportData.map(r => {
          if (r.id !== sourceId) return r;
          const { pairedWithId, pairedWithFile, ...rest } = r;
          return rest;
        });
        const fileInfo = savedFiles.find(f => f.name === reportName);
        await saveFile(uid, reportName, updated, { imageStorage: false, reportType: fileInfo?.reportType ?? reportType });
        setAllLoaded(prev => ({ ...prev, [reportName]: updated }));
      }
    } finally {
      setSaving(prev => ({ ...prev, [tx.id]: false }));
    }
  }, [uid, allLoaded, savedFiles]);

  // Save an edited transaction back to its source report only
  const handleEditSave = useCallback(async (updated) => {
    if (!editingTx || !uid) return;
    const { _reportName, _reportType, _sourceReports, _isDuplicate, _isLinked, ...clean } = updated;
    const reportName = _reportName ?? editingTx._reportName;
    const reportData = allLoaded[reportName];
    if (reportData) {
      const newData = reportData.map(r => r.id === clean.id ? clean : r);
      const fi = savedFiles.find(f => f.name === reportName);
      await saveFile(uid, reportName, newData, { imageStorage: false, reportType: fi?.reportType ?? _reportType });
      setAllLoaded(prev => ({ ...prev, [reportName]: newData }));
      onFilePatch?.(reportName, newData);
    }
    // Sync reimbursed flag to the paired item (linked expense↔transaction pairs)
    if (clean.pairedWithId && clean.pairedWithFile) {
      const pfi = savedFiles.find(f => f.name === clean.pairedWithFile);
      let pairedData = allLoaded[clean.pairedWithFile];
      if (!pairedData) {
        const loaded = await loadFile(uid, clean.pairedWithFile, pfi?.reportType ?? 'expense');
        pairedData = loaded?.data;
      }
      if (pairedData) {
        const newPairedData = pairedData.map(r =>
          String(r.id) === String(clean.pairedWithId)
            ? { ...r, reimbursed: clean.reimbursed || undefined }
            : r
        );
        await saveFile(uid, clean.pairedWithFile, newPairedData, { imageStorage: false, reportType: pfi?.reportType ?? 'expense' });
        setAllLoaded(prev => ({ ...prev, [clean.pairedWithFile]: newPairedData }));
        onFilePatch?.(clean.pairedWithFile, newPairedData);
      }
    }
    setEditingTx(null);
  }, [editingTx, uid, allLoaded, savedFiles, onFilePatch]);

  const handleDeleteExpense = useCallback(async (tx) => {
    if (!uid || tx._reportType !== 'expense' || tx._isLinked) return;
    const reportName = tx._reportName;
    const reportData = allLoaded[reportName];
    if (!reportData) return;
    const newData = reportData.filter(r => r.id !== tx.id);
    const fi = savedFiles.find(f => f.name === reportName);
    await saveFile(uid, reportName, newData, { imageStorage: false, reportType: fi?.reportType ?? 'expense' });
    setAllLoaded(prev => ({ ...prev, [reportName]: newData }));
  }, [uid, allLoaded, savedFiles]);

  const handleCopyExpense = useCallback(async (expense, targetFileName) => {
    if (!uid) return;
    const { _reportName, _reportType, _sourceReports, _isDuplicate, _isLinked, ...clean } = expense;
    const now = new Date().toISOString();
    const copied = {
      ...clean,
      id: Date.now(),
      imageId: clean.imageId ?? (clean.imagePreview && !clean.imagePreview.startsWith('data:') ? clean.id : undefined),
      createdAt: now, lastModifiedAt: now,
    };
    const existing = allLoaded[targetFileName]
      ?? (await loadFile(uid, targetFileName, 'expense'))?.data ?? [];
    const fi = savedFiles.find(f => f.name === targetFileName);
    await saveFile(uid, targetFileName, [copied, ...existing], { imageStorage: false, reportType: fi?.reportType ?? 'expense' });
    setAllLoaded(prev => ({ ...prev, [targetFileName]: [copied, ...(prev[targetFileName] ?? existing)] }));
  }, [uid, allLoaded, savedFiles]);

  const handleMoveExpense = useCallback(async (expense, targetFileName) => {
    if (!uid) return;
    const { _reportName, _reportType, _sourceReports, _isDuplicate, _isLinked, ...clean } = expense;
    const sourceName = _reportName ?? editingTx?._reportName;
    const now = new Date().toISOString();
    const moved = { ...clean, imageId: undefined, lastModifiedAt: now };
    const existing = allLoaded[targetFileName]
      ?? (await loadFile(uid, targetFileName, 'expense'))?.data ?? [];
    const targetFi = savedFiles.find(f => f.name === targetFileName);
    await saveFile(uid, targetFileName, [moved, ...existing], { imageStorage: false, reportType: targetFi?.reportType ?? 'expense' });
    setAllLoaded(prev => ({ ...prev, [targetFileName]: [moved, ...(prev[targetFileName] ?? existing)] }));
    if (sourceName && allLoaded[sourceName]) {
      const newSource = allLoaded[sourceName].filter(r => r.id !== clean.id);
      const sourceFi = savedFiles.find(f => f.name === sourceName);
      await saveFile(uid, sourceName, newSource, { imageStorage: false, reportType: sourceFi?.reportType ?? 'expense' });
      setAllLoaded(prev => ({ ...prev, [sourceName]: newSource }));
    }
    setEditingTx(null);
  }, [uid, allLoaded, savedFiles, editingTx]);

  // Link two transactions from the reconcile modal (same logic as confirmLink but takes args directly)
  const handleReconcileLink = useCallback(async (expenseTx, otherTx) => {
    if (!uid) return;
    const targetAmount = parseFloat(otherTx.convertedTotal ?? otherTx.total ?? otherTx.amount ?? 0);
    const isForex = expenseTx.currency && expenseTx.currency !== defaultCurrency;
    let updatedFields;
    if (isForex) {
      const originalTotal = parseFloat(expenseTx.total ?? 0);
      const newRate = originalTotal > 0 ? targetAmount / originalTotal : 1;
      updatedFields = { convertedTotal: targetAmount, conversionRate: newRate, convertedCurrency: defaultCurrency };
    } else {
      updatedFields = { total: targetAmount, convertedTotal: targetAmount };
    }
    const expData = allLoaded[expenseTx._reportName];
    if (expData) {
      const { _reportName, _reportType, _sourceReports, _isDuplicate, _isLinked, ...cleanBase } = expenseTx;
      const updExp = { ...cleanBase, ...updatedFields, pairedWithId: otherTx.id, pairedWithFile: otherTx._reportName };
      const updReport = expData.map(r => r.id === expenseTx.id ? updExp : r);
      const fi = savedFiles.find(f => f.name === expenseTx._reportName);
      await saveFile(uid, expenseTx._reportName, updReport, { imageStorage: false, reportType: fi?.reportType });
      setAllLoaded(prev => ({ ...prev, [expenseTx._reportName]: updReport }));
    }
    const otherData = allLoaded[otherTx._reportName];
    if (otherData) {
      const updOther = otherData.map(r =>
        r.id === otherTx.id ? { ...r, pairedWithId: expenseTx.id, pairedWithFile: expenseTx._reportName } : r
      );
      const fi = savedFiles.find(f => f.name === otherTx._reportName);
      await saveFile(uid, otherTx._reportName, updOther, { imageStorage: false, reportType: fi?.reportType });
      setAllLoaded(prev => ({ ...prev, [otherTx._reportName]: updOther }));
    }
  }, [uid, allLoaded, savedFiles, defaultCurrency]);

  // Accept a single AI-suggested classification for one transaction
  const handleClassifyOne = useCallback(async (tx, newClass) => {
    if (!uid) return;
    const reportData = allLoaded[tx._reportName];
    if (!reportData) return;
    const updated = reportData.map(r => r.id === tx.id ? { ...r, businessClassification: newClass } : r);
    const fi = savedFiles.find(f => f.name === tx._reportName);
    await saveFile(uid, tx._reportName, updated, { imageStorage: false, reportType: fi?.reportType ?? tx._reportType });
    setAllLoaded(prev => ({ ...prev, [tx._reportName]: updated }));
  }, [uid, allLoaded, savedFiles]);

  // Confirm manual link: reconcile amounts then save pairedWithId on both
  const confirmLink = useCallback(async () => {
    if (linkSelected.length !== 2) return;
    const expenseTx = linkSelected.find(tx => tx._reportType === 'expense');
    const otherTx = linkSelected.find(tx => tx._reportType !== 'expense');
    if (!expenseTx || !otherTx) return;

    setLinkSaving(true);
    try {
      // Target amount is the CC/bank converted total (already in default currency)
      const targetAmount = parseFloat(otherTx.convertedTotal ?? otherTx.total ?? otherTx.amount ?? 0);
      const isForex = expenseTx.currency && expenseTx.currency !== defaultCurrency;

      let updatedFields;
      if (isForex) {
        // Keep original foreign-currency amount; adjust rate so convertedTotal matches
        const originalTotal = parseFloat(expenseTx.total ?? 0);
        const newRate = originalTotal > 0 ? targetAmount / originalTotal : 1;
        updatedFields = { convertedTotal: targetAmount, conversionRate: newRate, convertedCurrency: defaultCurrency };
      } else {
        updatedFields = { total: targetAmount, convertedTotal: targetAmount };
      }

      // Save expense report with updated amounts + pairedWithId + pairedWithFile
      const expData = allLoaded[expenseTx._reportName];
      if (expData) {
        const { _reportName, _reportType, _sourceReports, _isDuplicate, _isLinked, ...cleanBase } = expenseTx;
        const updExpense = { ...cleanBase, ...updatedFields, pairedWithId: otherTx.id, pairedWithFile: otherTx._reportName };
        const updExpenseReport = expData.map(r => r.id === expenseTx.id ? updExpense : r);
        const fi = savedFiles.find(f => f.name === expenseTx._reportName);
        await saveFile(uid, expenseTx._reportName, updExpenseReport, { imageStorage: false, reportType: fi?.reportType });
        setAllLoaded(prev => ({ ...prev, [expenseTx._reportName]: updExpenseReport }));
      }

      // Save CC/bank report with pairedWithId + pairedWithFile
      const otherData = allLoaded[otherTx._reportName];
      if (otherData) {
        const updOtherReport = otherData.map(r =>
          r.id === otherTx.id ? { ...r, pairedWithId: expenseTx.id, pairedWithFile: expenseTx._reportName } : r
        );
        const fi = savedFiles.find(f => f.name === otherTx._reportName);
        await saveFile(uid, otherTx._reportName, updOtherReport, { imageStorage: false, reportType: fi?.reportType });
        setAllLoaded(prev => ({ ...prev, [otherTx._reportName]: updOtherReport }));
      }

      setLinkSelected([]);
      setLinkMode(false);
    } finally {
      setLinkSaving(false);
    }
  }, [linkSelected, allLoaded, savedFiles, uid, defaultCurrency]);

  // Helpers for date range filtering
  const inDateRange = useCallback((date) => {
    if (!date) return true;
    if (dateFrom && date < dateFrom) return false;
    if (dateTo && date > dateTo) return false;
    return true;
  }, [dateFrom, dateTo]);

  // Visible transactions: date range + classification + account filters
  const visibleTransactions = useMemo(() => {
    return classifiedTransactions.filter(tx => {
      if (!inDateRange(tx.date)) return false;
      const acctKey = tx._reportType === 'expense' ? tx._reportName : (fileToAccount.get(tx._reportName) ?? tx._reportName);
      if (!filterAccounts.has(acctKey)) return false;
      return filterSet.has(tx.businessClassification);
    });
  }, [classifiedTransactions, filterSet, filterAccounts, fileToAccount, inDateRange]);

  // Merge cross-report duplicates into single display rows
  const displayTransactions = useMemo(() => {
    const result = [];
    const used = new Set();
    for (let i = 0; i < visibleTransactions.length; i++) {
      if (used.has(i)) continue;
      const tx = visibleTransactions[i];
      const group = [{ tx, idx: i }];
      for (let j = i + 1; j < visibleTransactions.length; j++) {
        if (used.has(j)) continue;
        const other = visibleTransactions[j];
        if (other._reportName === tx._reportName) continue;

        // Explicit pairing always merges, regardless of amount/date/vendor
        const explicitlyPaired = (tx.pairedWithId && tx.pairedWithId === other.id) ||
          (other.pairedWithId && other.pairedWithId === tx.id);
        if (!explicitlyPaired) {
          // Duplicates only make sense within the same collection type
          if (tx._reportType !== other._reportType) continue;
          // Direction must match: credit ↔ credit, debit ↔ debit
          // Expense-report items without a direction default to 'debit'
          const dirA = tx.transactionDirection ?? 'debit';
          const dirB = other.transactionDirection ?? 'debit';
          if (dirA !== dirB) continue;
          // Exact same amount (float-safe)
          const amtA = parseFloat(tx.convertedTotal ?? tx.total ?? tx.amount ?? 0);
          const amtB = parseFloat(other.convertedTotal ?? other.total ?? other.amount ?? 0);
          if (Math.abs(amtA - amtB) >= 0.001) continue;
          if (tx.date && other.date &&
            Math.abs(new Date(tx.date) - new Date(other.date)) > 2 * 86400000) continue;
          const vA = (tx.vendor || '').trim().toLowerCase();
          const vB = (other.vendor || '').trim().toLowerCase();
          if (vA !== vB) continue;
        }
        group.push({ tx: other, idx: j });
        used.add(j);
      }
      used.add(i);

      // Merge notes from all source transactions
      const allNotes = group
        .map(g => g.tx.notes)
        .filter(note => note && note.trim())
        .map(note => note.trim())
        .filter((note, index, arr) => arr.indexOf(note) === index); // deduplicate

      const mergedNotes = allNotes.length > 0 ? allNotes.join(' | ') : undefined;

      // For explicitly linked pairs, use the CC/bank side as the base so the
      // authoritative charge amount is used rather than the manually-entered receipt amount.
      const hasLink = group.some(g => g.tx.pairedWithId != null);
      const baseGroup = hasLink
        ? [...group].sort((a, b) => (a.tx._reportType !== 'expense' ? -1 : b.tx._reportType !== 'expense' ? 1 : 0))
        : group;

      result.push({
        ...baseGroup[0].tx,
        notes: mergedNotes,
        _sourceReports: group.map(g => ({
          name: g.tx._reportName,
          type: g.tx._reportType,
          id: g.tx.id,
          pairedWithId: g.tx.pairedWithId ?? null,
          vendor: g.tx.vendor ?? null,
        })),
        _isDuplicate: group.length > 1,
        _isLinked: group.length > 1 && group.some(g => g.tx.pairedWithId != null),
      });

    }
    return result;
  }, [visibleTransactions]);

  // Sort then group the display list
  const sortedTransactions = useMemo(() =>
    sortTransactions(displayTransactions, sortBy),
    [displayTransactions, sortBy]);

  const groupedTransactions = useMemo(() => {
    if (groupBy === 'none') return [{ key: null, label: null, items: sortedTransactions }];
    const ctx = { fileToAccount };
    const map = new Map();
    for (const tx of sortedTransactions) {
      const k = getGroupKey(tx, groupBy, ctx);
      if (!map.has(k)) map.set(k, []);
      map.get(k).push(tx);
    }
    const entries = Array.from(map.entries())
      .filter(([k]) => k !== null)
      .map(([k, items]) => ({ key: k, label: groupLabel(k, groupBy), items }));
    if (groupBy === 'source') entries.sort((a) => a.key === 'expense' ? -1 : 1);
    return entries;
  }, [sortedTransactions, groupBy, fileToAccount]);

  // When first item is selected in link mode, score every other visible tx as a candidate
  // Score: +1 vendor fuzzy match, +1 amount within 5% or $2, +1 date within 7 days
  const linkCandidates = useMemo(() => {
    if (!linkMode || linkSelected.length !== 1) return new Map();
    const sel = linkSelected[0];
    const selIsExpense = sel._reportType === 'expense';
    const selAmt = parseFloat(sel.convertedTotal ?? sel.total ?? sel.amount ?? 0);
    const selVendor = (sel.vendor || '').trim().toLowerCase();
    const selDate = sel.date ? new Date(sel.date).getTime() : null;

    const map = new Map();
    for (const tx of displayTransactions) {
      if (tx.id === sel.id) continue;
      // Must be a different report AND opposite type (expense ↔ non-expense)
      const txIsExpense = tx._reportType === 'expense';
      if (selIsExpense === txIsExpense) continue;

      let score = 0;
      const txVendor = (tx.vendor || '').trim().toLowerCase();
      if (selVendor && txVendor && (selVendor === txVendor || selVendor.includes(txVendor) || txVendor.includes(selVendor))) score += 1;

      const txAmt = parseFloat(tx.convertedTotal ?? tx.total ?? tx.amount ?? 0);
      if (selAmt > 0 && (Math.abs(selAmt - txAmt) / selAmt <= 0.05 || Math.abs(selAmt - txAmt) <= 2)) score += 1;

      const txDate = tx.date ? new Date(tx.date).getTime() : null;
      if (selDate && txDate && Math.abs(selDate - txDate) <= 7 * 86400000) score += 1;

      if (score > 0) map.set(tx.id, score);
    }
    return map;
  }, [linkMode, linkSelected, displayTransactions]);

  // P&L base — date range + account filter only, independent of filterSet
  const pnlTransactions = useMemo(() => {
    return classifiedTransactions.filter(tx => {
      if (!inDateRange(tx.date)) return false;
      const acctKey = tx._reportType === 'expense' ? tx._reportName : (fileToAccount.get(tx._reportName) ?? tx._reportName);
      if (!filterAccounts.has(acctKey)) return false;
      return true;
    });
  }, [classifiedTransactions, inDateRange, filterAccounts, fileToAccount]);

  // P&L — scoped to date range, ignores filterSet so toggling classifications doesn't zero out P&L
  const pnl = useMemo(() => {
    let income = 0, expenses = 0;
    for (const tx of pnlTransactions) {
      if (tx.pairedWithId && tx._reportType === 'expense') continue;
      const cls = tx.businessClassification;
      const amount = parseFloat(tx.convertedTotal ?? tx.total ?? tx.amount ?? 0);
      if (cls === 'business_income') income += amount;
      else if (cls === 'business_expense') expenses += amount;
      else if (cls === 'business_expense_refund') expenses -= amount;
    }
    return { income, expenses, net: income - expenses };
  }, [pnlTransactions]);

  // Raw totals across all currently-displayed transactions regardless of classification
  const rawTotals = useMemo(() => {
    let credits = 0, debits = 0;
    for (const tx of displayTransactions) {
      const amt = parseFloat(tx.convertedTotal ?? tx.total ?? tx.amount ?? 0);
      const isCredit = tx.transactionDirection === 'credit' || tx.transactionType === 'credit';
      if (isCredit) credits += amt; else debits += amt;
    }
    return { credits, debits, net: credits - debits };
  }, [displayTransactions]);

  const unclassifiedCount = useMemo(() =>
    classifiedTransactions.filter(tx => inDateRange(tx.date) && UNCLASSIFIED_KEYS.has(tx.businessClassification)).length,
    [classifiedTransactions, inDateRange]);

  const toggleFile = (name) => {
    startTransition(() => {
      setDeselected(prev => {
        const next = new Set(prev);
        if (next.has(name)) next.delete(name); else next.add(name);
        return next;
      });
    });
  };

  const currentSelected = selectedFiles;

  return (
    <div style={{
      position: 'fixed', inset: 0, zIndex: 500,
      background: T.appBg, display: 'flex', flexDirection: 'column',
      fontFamily: T.fontBody,
    }}>
      {/* Top bar */}
      <div style={{ background: T.gradNavy, padding: '14px 16px 12px', flexShrink: 0 }}>
        {topBarProps && <FinanceViewTopBar {...topBarProps} />}
        <div style={{ display: 'flex', alignItems: 'center', gap: 10, marginTop: 8 }}>
          <button
            onClick={onGoToDashboard}
            style={{ background: 'rgba(255,255,255,0.12)', border: 'none', borderRadius: 8, padding: '6px 10px', cursor: 'pointer', display: 'flex', alignItems: 'center', gap: 4, color: '#fff', fontSize: 13, fontWeight: 600 }}
          >
            <ArrowLeft size={14} color="#fff" /> Dashboard
          </button>
          <div style={{ color: '#fff', fontSize: 16, fontWeight: 800 }}>My Finances</div>
        </div>
        {/*  <div style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
           {/* <button onClick={onBack} style={{ background: 'rgba(255,255,255,0.15)', border: 'none', borderRadius: 8, padding: '6px 10px', cursor: 'pointer', color: '#fff', display: 'flex', alignItems: 'center' }}>
            <ArrowLeft size={18} />
          </button>
          <div style={{ color: '#fff', fontWeight: 800, fontSize: 17, flex: 1 }}>My Finances</div> 
           <button
            onClick={() => setShowReconcile(true)}
            title="Reconcile transactions"
            style={{ background: 'rgba(255,255,255,0.15)', border: 'none', borderRadius: 8, padding: '6px 10px', cursor: 'pointer', color: '#fff', display: 'flex', alignItems: 'center', gap: 5, fontSize: 13, fontWeight: 700 }}
          >
            <GitMerge size={14} /> Reconcile
          </button> 
           <button
            onClick={loadSelected}
            title="Reload from server"
            style={{ background: 'rgba(255,255,255,0.15)', border: 'none', borderRadius: 8, padding: '6px 10px', cursor: 'pointer', color: '#fff', display: 'flex', alignItems: 'center' }}
          >
            <RefreshCw size={14} />
          </button> 
        </div> */}
        {/* <FinanceViewNav
          current="combined"
          onGoToDashboard={onGoToDashboard}
          onGoToFinancial={onGoToFinancial}
          // onGoToTax={onGoToTax}
          // onGoToExpenses={onGoToExpenses}
          // onGoToAdd={onGoToAdd}
          onGoToCapture={onGoToCapture}
        // onImportCreditCard={onImportCreditCard}
        // onImportBank={() => { } }
        /> */}
        {initialFilters?.title && (
          <div style={{ marginTop: 10, color: '#fff', fontSize: 18, fontWeight: 800, letterSpacing: -0.3 }}>
            {initialFilters.title}
          </div>
        )}

        <DateRangePicker
          dateFrom={dateFrom}
          dateTo={dateTo}
          onChange={(from, to) => { setDateFrom(from); setDateTo(to); }}
          variant="dark"
        />
      </div>

      <div style={{ flexShrink: 0, height: (!controlsCollapsed && classificationsOpen) ? topHeight : 'auto', overflowY: (!controlsCollapsed && classificationsOpen) ? 'auto' : 'visible', borderBottom: '1px solid #E5E7EB' }}>


        {!loading && currentSelected.length > 0 && (
          <>
            {/* P&L Cards */}
            {/* <div style={{ display: 'flex', gap: 8, marginBottom: 14 }}>
              {[
                { label: 'Biz Income', value: pnl.income, color: '#15803d', bg: '#F0FDF4', border: '#BBF7D0', Icon: TrendingUp },
                { label: 'Biz Expenses', value: pnl.expenses, color: '#dc2626', bg: '#FEF2F2', border: '#FECACA', Icon: TrendingDown },
                {
                  label: 'Net', value: Math.abs(pnl.net),
                  color: pnl.net >= 0 ? '#15803d' : '#dc2626',
                  bg: pnl.net >= 0 ? '#F0FDF4' : '#FEF2F2',
                  border: pnl.net >= 0 ? '#BBF7D0' : '#FECACA',
                  Icon: Minus,
                },
              ].map(({ label, value, color, bg, border, Icon }) => (
                <div key={label} style={{ flex: 1, background: bg, borderRadius: 12, padding: '10px 12px', border: `1px solid ${border}` }}>
                  <div style={{ display: 'flex', alignItems: 'center', gap: 5, marginBottom: 3 }}>
                    <Icon size={12} color={color} />
                    <span style={{ fontSize: 10, fontWeight: 700, color, textTransform: 'uppercase', letterSpacing: 0.4 }}>{label}</span>
                  </div>
                  <div style={{ fontSize: 18, fontWeight: 900, color, ...amountStyle() }}>
                    {formatAmount(value, defaultCurrency)}
                  </div>
                </div>
              ))}
            </div> */}



            {/* Unclassified banner */}
            {false && unclassifiedCount > 0 && (
              <div style={{
                background: '#FFFBEB', border: '1px solid #FDE68A', borderRadius: 8,
                padding: '10px 14px', marginBottom: 10,
                display: 'flex', alignItems: 'center', justifyContent: 'space-between',
              }}>
                <span style={{ fontSize: 13, color: '#92400e', fontWeight: 600 }}>
                  {unclassifiedCount} transaction{unclassifiedCount !== 1 ? 's' : ''} need classification
                </span>
                <button
                  onClick={() => setFilterSet(new Set(['unclassified', 'unclassified_credit', 'unclassified_debit']))}
                  style={{ fontSize: 12, color: '#b45309', fontWeight: 700, background: 'none', border: 'none', cursor: 'pointer', textDecoration: 'underline' }}
                >
                  Review
                </button>
              </div>
            )}
            {/* const clsConfig = BIZ_CLASS[cls] || BIZ_CLASS.unclassified_debit; */}
          </>
        )}

        {/* ── Collapse toggle strip ────────────────────────────── */}
        <div style={{
          display: 'flex', alignItems: 'center',
          background: '#F9FAFB',
          borderBottom: controlsCollapsed ? 'none' : '1px solid #F3F4F6',
        }}>
          <button
            onClick={() => setControlsCollapsed(v => !v)}
            style={{
              flex: 1, display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 5,
              padding: '4px 14px', background: 'transparent',
              border: 'none', cursor: 'pointer', color: '#9CA3AF', fontSize: 11, fontWeight: 700,
            }}
          >
            <ChevronDown size={13} style={{ transform: controlsCollapsed ? 'rotate(-90deg)' : 'rotate(0deg)', transition: 'transform 0.15s' }} />
            {controlsCollapsed ? 'Show filters' : 'Hide filters'}
          </button>
          <button
            onClick={() => { setLinkMode(m => !m); setLinkSelected([]); }}
            title="Link a receipt to a bank/CC charge"
            style={{
              display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 3,
              padding: '4px 10px', background: linkMode ? T.tealPale : 'transparent',
              border: 'none', borderLeft: '1px solid #E5E7EB', cursor: 'pointer',
              color: linkMode ? T.tealDeep : '#9CA3AF',
              fontSize: 11, fontWeight: 700,
            }}
          >
            <Link2 size={13} /> Link
          </button>
          <button
            onClick={() => setShowReconcile(true)}
            title="AI-assisted link tool"
            style={{
              display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 3,
              padding: '4px 10px', background: 'transparent',
              border: 'none', borderLeft: '1px solid #E5E7EB', cursor: 'pointer', color: '#9CA3AF',
              fontSize: 11, fontWeight: 700,
            }}
          >
            <Link2 size={13} /> AI
          </button>
          <button
            onClick={loadSelected}
            title="Reload from server"
            style={{
              display: 'flex', alignItems: 'center', justifyContent: 'center',
              padding: '4px 10px', background: 'transparent',
              border: 'none', borderLeft: '1px solid #E5E7EB', cursor: 'pointer', color: '#9CA3AF',
            }}
          >
            <RefreshCw size={13} />
          </button>
          <button
            onClick={() => setShowHelp(true)}
            title="Help"
            style={{
              display: 'flex', alignItems: 'center', justifyContent: 'center',
              padding: '4px 10px', background: 'transparent',
              border: 'none', borderLeft: '1px solid #E5E7EB', cursor: 'pointer', color: '#9CA3AF',
            }}
          >
            <HelpCircle size={13} />
          </button>
        </div>
        {!controlsCollapsed && <div style={{ padding: '14px 14px 0' }}>

          {/* ── Report selector ──────────────────────────────────── */}
 
          {/* -- sorting and grouping ---------------------------------------------------*/}
        <div style={{ display: 'flex', gap: 6, flexWrap: 'wrap', alignItems: 'center', marginBottom: 6 }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: 4 }}>
            <ArrowUpDown size={14} color="#9CA3AF" />
            <select
              value={sortBy}
              onChange={e => setSortBy(e.target.value)}
              style={{ fontSize: 14, padding: '4px 7px', borderRadius: 6, border: '1px solid #D1D5DB', background: '#fff' }}
            >
              {SORT_OPTIONS.map(o => <option key={o.value} value={o.value}>{o.label}</option>)}
            </select>
          </div>
          <div style={{ display: 'flex', alignItems: 'center', gap: 4 }}>
            <Layers size={14} color="#9CA3AF" />
            <select
              value={groupBy}
              onChange={e => { setGroupBy(e.target.value); setAllCollapse(e.target.value !== 'none'); setCollapsedGroups(new Set()); }}
              style={{ fontSize: 14, padding: '4px 7px', borderRadius: 6, border: '1px solid #D1D5DB', background: '#fff' }}
            >
              {GROUP_OPTIONS.map(o => <option key={o.value} value={o.value}>{o.label}</option>)}
            </select>
            {groupBy !== 'none' && (() => {
              const allKeys = groupedTransactions.map(g => g.key).filter(k => k != null);
              const allCollapsed = allCollapse
                ? collapsedGroups.size === 0
                : allKeys.length > 0 && allKeys.every(k => collapsedGroups.has(k));
              return (
                <button
                  onClick={() => {
                    if (allCollapsed) { setAllCollapse(false); setCollapsedGroups(new Set()); }
                    else { setAllCollapse(true); setCollapsedGroups(new Set()); }
                  }}
                  style={{ fontSize: 13, fontWeight: 700, padding: '3px 8px', borderRadius: 6, border: '1px solid #D1D5DB', background: '#fff', cursor: 'pointer', color: '#374151' }}
                >
                  {allCollapsed ? 'Expand all' : 'Collapse all'}
                </button>
              );
            })()}
          </div>
        </div>

          {/* --Classifications, Reports and Files -----------------------*/}
          <div style={{
            display: 'flex', alignItems: 'center',
            background: '#F9FAFB',
            borderBottom: controlsCollapsed ? 'none' : '1px solid #F3F4F6',
          }}>

            <button
              onClick={() => setClassificationsOpen(o => !o)}
              style={{
                flex: 1, display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 5,
                padding: '4px 14px', background: 'transparent',
                border: 'none', cursor: 'pointer', color: '#9CA3AF', fontSize: 11, fontWeight: 700,
              }}
            >
              <ChevronDown size={13} style={{ transform: classificationsOpen ? 'rotate(-90deg)' : 'rotate(0deg)', transition: 'transform 0.15s' }} />
               Select Classifications, Reports and Files
            </button>
          </div>

          {classificationsOpen && currentSelected.length > 0 && (<>
            {/* Sort + Group + Classification + Account + Link controls + Date range*/}
            <div style={{ display: 'flex', gap: 6, marginBottom: 10, flexWrap: 'wrap', alignItems: 'flex-start' }}>

              {/* Classifications Selector */
                (() => {
                  const classKeys = Object.keys(BIZ_CLASS).filter(k =>
                    classifiedTransactions.some(tx => inDateRange(tx.date) && tx.businessClassification === k)
                  );
                  return (
                    <div style={{ display: 'flex', flexDirection: 'column', gap: 3 }}>
                      <div style={{ fontSize: 10, fontWeight: 700, color: '#9CA3AF', textTransform: 'uppercase', letterSpacing: 0.5 }}>Classifications</div>
                      <div style={{ border: '1px solid #D1D5DB', borderRadius: 6, background: '#fff', minWidth: 172, overflow: 'hidden' }}>
                        {classKeys.map((k, i) => {
                          const c = BIZ_CLASS[k];
                          const on = filterSet.has(k);
                          const count = classifiedTransactions.filter(tx => inDateRange(tx.date) && tx.businessClassification === k).length;
                          return (
                            <div
                              key={k}
                              onClick={() => setFilterSet(prev => { const next = new Set(prev); next.has(k) ? next.delete(k) : next.add(k); return next; })}
                              style={{
                                display: 'flex', alignItems: 'center', gap: 6,
                                padding: '4px 8px', cursor: 'pointer',
                                background: on ? c.bg : '#fff',
                                borderBottom: i < classKeys.length - 1 ? '1px solid #F3F4F6' : 'none',
                              }}
                            >
                              <div style={{ width: 8, height: 8, borderRadius: '50%', background: c.color, flexShrink: 0 }} />
                              <span style={{ fontSize: 12, fontWeight: on ? 700 : 400, color: on ? c.color : '#374151', flex: 1 }}>{c.label}</span>
                              <span style={{ fontSize: 11, color: '#9CA3AF' }}>{count}</span>
                            </div>
                          );
                        })}
                      </div>
                      <div style={{ display: 'flex', gap: 4 }}>
                        <button onClick={() => setFilterSet(new Set([...CLASS_SELECT_KEYS, 'unclassified']))} style={{ fontSize: 10, padding: '2px 7px', borderRadius: 4, border: '1px solid #D1D5DB', background: '#fff', cursor: 'pointer', color: '#374151' }}>Select All</button>
                        <button onClick={() => setFilterSet(new Set())} style={{ fontSize: 10, padding: '2px 7px', borderRadius: 4, border: '1px solid #D1D5DB', background: '#fff', cursor: 'pointer', color: '#374151' }}>Select None</button>
                      </div>
                    </div>
                  );
                })()}



              {/* Account Groups Selector */}
              {currentSelected.length > 1 && (() => {
                const ROW_H = 26;
                const sortByDate = (names) => [...names].sort((a, b) => {
                  const fa = savedFiles.find(f => f.name === a);
                  const fb = savedFiles.find(f => f.name === b);
                  const ta = fa?.updatedAt ? new Date(fa.updatedAt).getTime() : 0;
                  const tb = fb?.updatedAt ? new Date(fb.updatedAt).getTime() : 0;
                  return tb - ta;
                });
                const expenseNames = sortByDate(currentSelected.filter(n => expenseFiles.some(f => f.name === n)));
                // Derive unique account names for tx files that have at least one selected file
                const txAccountNames = accountGroups
                  .filter(g => g.files.some(f => currentSelected.includes(f.name)))
                  .sort((a, b) => (b.lastImport ?? 0) - (a.lastImport ?? 0))
                  .map(g => g.accountName);
                const addGroup = (names) => setFilterAccounts(prev => new Set([...prev, ...names]));
                const removeGroup = (names) => setFilterAccounts(prev => { const next = new Set(prev); names.forEach(n => next.delete(n)); return next; });
                const renderGroup = (label, names) => names.length === 0 ? null : (
                  <div style={{ display: 'flex', flexDirection: 'column', gap: 3 }}>
                    <div style={{ fontSize: 10, fontWeight: 700, color: '#9CA3AF', textTransform: 'uppercase', letterSpacing: 0.5 }}>{label}</div>
                    <div style={{ border: '1px solid #D1D5DB', borderRadius: 6, background: '#fff', minWidth: 172, maxHeight: ROW_H * 4, overflowY: 'auto' }}>
                      {names.map((n, i) => {
                        const on = filterAccounts.has(n);
                        return (
                          <div
                            key={n}
                            onClick={() => setFilterAccounts(prev => { const next = new Set(prev); next.has(n) ? next.delete(n) : next.add(n); return next; })}
                            style={{
                              display: 'flex', alignItems: 'center', gap: 6,
                              padding: '4px 8px', cursor: 'pointer', height: ROW_H,
                              background: on ? '#EFF6FF' : '#fff',
                              borderBottom: i < names.length - 1 ? '1px solid #F3F4F6' : 'none',
                              boxSizing: 'border-box',
                            }}
                          >
                            <span style={{ fontSize: 12, fontWeight: on ? 700 : 400, color: on ? '#0369a1' : '#374151', flex: 1, whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>{n}</span>
                          </div>
                        );
                      })}
                    </div>
                    <div style={{ display: 'flex', gap: 4 }}>
                      <button onClick={() => addGroup(names)} style={{ fontSize: 10, padding: '2px 7px', borderRadius: 4, border: '1px solid #D1D5DB', background: '#fff', cursor: 'pointer', color: '#374151' }}>Select All</button>
                      <button onClick={() => removeGroup(names)} style={{ fontSize: 10, padding: '2px 7px', borderRadius: 4, border: '1px solid #D1D5DB', background: '#fff', cursor: 'pointer', color: '#374151' }}>Select None</button>
                    </div>
                  </div>
                );
                const renderTxGroup = (txNames) => txNames.length === 0 ? null : (
                  <div style={{ display: 'flex', flexDirection: 'column', gap: 3 }}>
                    <div style={{ fontSize: 10, fontWeight: 700, color: '#9CA3AF', textTransform: 'uppercase', letterSpacing: 0.5 }}>Transaction Accounts</div>
                    <div style={{ border: '1px solid #D1D5DB', borderRadius: 6, background: '#fff', minWidth: 172, maxHeight: ROW_H * 4, overflowY: 'auto' }}>
                      {txNames.map((n, i) => {
                        const on = filterAccounts.has(n);
                        const acctGroup = accountGroups.find(g => g.accountName === n);
                        const latestFile = acctGroup?.files.slice().sort((a, b) => (b.updatedAt ?? 0) > (a.updatedAt ?? 0) ? 1 : -1)[0];
                        return (
                          <div
                            key={n}
                            onClick={() => setFilterAccounts(prev => { const next = new Set(prev); next.has(n) ? next.delete(n) : next.add(n); return next; })}
                            style={{
                              display: 'flex', alignItems: 'center', gap: 6,
                              padding: '4px 8px', cursor: 'pointer', height: ROW_H,
                              background: on ? '#EFF6FF' : '#fff',
                              borderBottom: i < txNames.length - 1 ? '1px solid #F3F4F6' : 'none',
                              boxSizing: 'border-box',
                            }}
                          >
                            <span style={{ fontSize: 12, fontWeight: on ? 700 : 400, color: on ? '#0369a1' : '#374151', flex: 1, whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>{n}</span>
                            {onGoToTransactionAccount && latestFile && (
                              <button
                                onClick={e => { e.stopPropagation(); onGoToTransactionAccount(latestFile.name); }}
                                style={{ fontSize: 10, padding: '1px 5px', borderRadius: 4, border: '1px solid #D1D5DB', background: '#fff', cursor: 'pointer', color: '#374151', flexShrink: 0 }}
                                title={`View ${n}`}
                              >View</button>
                            )}
                          </div>
                        );
                      })}
                    </div>
                    <div style={{ display: 'flex', gap: 4 }}>
                      <button onClick={() => addGroup(txNames)} style={{ fontSize: 10, padding: '2px 7px', borderRadius: 4, border: '1px solid #D1D5DB', background: '#fff', cursor: 'pointer', color: '#374151' }}>Select All</button>
                      <button onClick={() => removeGroup(txNames)} style={{ fontSize: 10, padding: '2px 7px', borderRadius: 4, border: '1px solid #D1D5DB', background: '#fff', cursor: 'pointer', color: '#374151' }}>Select None</button>
                    </div>
                  </div>
                );
                return (
                  <div style={{ display: 'flex', flexDirection: windowWidth >= 600 ? 'row' : 'column', gap: 8, alignItems: 'flex-start' }}>
                    {renderGroup('Expense Reports', expenseNames)}
                    {renderTxGroup(txAccountNames)}
                  </div>
                );
              })()}


            </div>
            {linkMode && (
              <div style={{ fontSize: 14, color: '#0369a1', background: '#EFF6FF', border: '1px solid #BFDBFE', borderRadius: 8, padding: '8px 12px', marginBottom: 8 }}>
                Select one <strong>expense report</strong> item and one <strong>CC/bank</strong> item to link as the same charge.
                {linkSelected.length > 0 && ` (${linkSelected.length}/2 selected)`}
              </div>
            )}
          </>)}


      </div>}
      </div>

      {/* ── Drag handle (only active when classifications panel is open) ──── */}
      {(!controlsCollapsed && classificationsOpen) ? (
        <div
          onPointerDown={(e) => {
            e.preventDefault();
            isDragging.current = true;
            dragStartY.current = e.clientY;
            dragStartHeight.current = topHeight;
            e.currentTarget.setPointerCapture(e.pointerId);
            document.body.style.userSelect = 'none';
            document.body.style.cursor = 'ns-resize';
          }}
          onPointerMove={(e) => {
            if (!isDragging.current) return;
            const delta = e.clientY - dragStartY.current;
            const newHeight = Math.max(60, Math.min(window.innerHeight - 120, dragStartHeight.current + delta));
            setTopHeight(newHeight);
          }}
          onPointerUp={() => {
            isDragging.current = false;
            document.body.style.userSelect = '';
            document.body.style.cursor = '';
          }}
          style={{
            flexShrink: 0, height: 20, cursor: 'ns-resize', touchAction: 'none',
            background: '#F3F4F6', borderBottom: '1px solid #E5E7EB',
            display: 'flex', alignItems: 'center', justifyContent: 'center',
          }}
        >
          <div style={{ width: 36, height: 3, borderRadius: 2, background: '#D1D5DB' }} />
        </div>
      ) : (
        <div style={{ flexShrink: 0, height: 4, background: '#F3F4F6', borderBottom: '1px solid #E5E7EB' }} />
      )}

      {/* ── Scrollable transaction list ───────────────────────────────────── */}


      <div style={{ flex: 1, overflowY: 'auto', padding: '8px 14px 80px' }}>

        {loading && (
          <div style={{ textAlign: 'center', padding: 40, color: '#6B7280', fontSize: 14 }}>Loading transactions…</div>
        )}
            {/* Raw net — all currently shown transactions */}
            <div style={{
              display: 'flex', alignItems: 'center', justifyContent: 'space-between',
              background: '#F9FAFB', border: '1px solid #E5E7EB', borderRadius: 10,
              padding: '8px 14px', marginBottom: 10, gap: 8,
            }}>
              <span style={{ fontSize: 11, color: '#6B7280', fontWeight: 600 }}>
                All {displayTransactions.length} items
              </span>
              <div style={{ display: 'flex', alignItems: 'center', gap: 12 }}>
                <span style={{ fontSize: 11, color: '#15803d' }}>
                  +{formatAmount(rawTotals.credits, defaultCurrency)}
                </span>
                <span style={{ fontSize: 11, color: '#dc2626' }}>
                  −{formatAmount(rawTotals.debits, defaultCurrency)}
                </span> {'='}
                <span style={{
                  fontSize: 15, fontWeight: 1000, ...amountStyle(),
                  color: rawTotals.net >= 0 ? '#15803d' : '#dc2626',
                }}>
                  {rawTotals.net >= 0 ? '' : '−'}{formatAmount(Math.abs(rawTotals.net), defaultCurrency)}
                </span>
              </div>
            </div>
        {!loading && currentSelected.length > 0 && (
          <div style={{ display: 'flex', flexDirection: 'column', gap: 4 }}>
            {displayTransactions.length === 0 ? (
              <div style={{ textAlign: 'center', padding: 40, color: '#9CA3AF', fontSize: 14 }}>
                No transactions match this filter.
              </div>
            ) : groupedTransactions.map(({ key, label, items }) => {
              // When first link item is picked, show only the selected + scored candidates
              const selItem0 = linkMode && linkSelected.length === 1 ? linkSelected[0] : null;
              const visibleItems = selItem0
                ? items.filter(tx =>
                  linkSelected.some(s => s.id === tx.id) ||
                  (linkCandidates.get(tx.id) ?? 0) > 0
                )
                : items;
              if (visibleItems.length === 0) return null;

              // Compute group net totals
              const grp = groupNetTotals(visibleItems);

              return (
                <div key={key ?? '__all'}>
                  {label && (() => {
                    const isCollapsed = allCollapse ? !collapsedGroups.has(key) : collapsedGroups.has(key);
                    const toggleCollapse = () => setCollapsedGroups(prev => {
                      const next = new Set(prev);
                      if (next.has(key)) next.delete(key); else next.add(key);
                      return next;
                    });
                    return (
                      <div
                        onClick={toggleCollapse}
                        style={{
                          display: 'flex', alignItems: 'center', justifyContent: 'space-between',
                          padding: '6px 4px 4px', marginTop: key ? 8 : 0,
                          cursor: 'pointer', userSelect: 'none',
                        }}
                      >
                        <span style={{ display: 'flex', alignItems: 'center', gap: 5 }}>
                          <ChevronDown size={13} color="#9CA3AF" style={{ transform: isCollapsed ? 'rotate(-90deg)' : 'none', transition: 'transform 0.15s', flexShrink: 0 }} />
                          <span style={{ fontSize: 11, fontWeight: 800, color: '#374151', textTransform: 'uppercase', letterSpacing: 0.5 }}>
                            {label}
                          </span>
                          <span style={{ fontSize: 10, color: '#9CA3AF' }}>({visibleItems.length})</span>
                        </span>
                        <span style={{ fontSize: 11, ...amountStyle(), display: 'flex', alignItems: 'center', gap: 8 }}>
                          {grp.credits > 0 && <span style={{ color: '#15803d' }}>+{formatAmount(grp.credits, defaultCurrency)}</span>}
                          {grp.debits > 0 && <span style={{ color: '#dc2626' }}>−{formatAmount(grp.debits, defaultCurrency)}</span>}
                          = <span style={{ fontSize: 13, fontWeight: 800, color: grp.net >= 0 ? '#15803d' : '#dc2626' }}>
                            {grp.net >= 0 ? '' : '−'}{formatAmount(Math.abs(grp.net), defaultCurrency)}
                          </span>
                        </span>
                      </div>
                    );
                  })()}
                  {!(allCollapse ? !collapsedGroups.has(key) : collapsedGroups.has(key)) && visibleItems.map(tx => {
                    const cls = tx.businessClassification || 'unclassified_debit';
                    const pending = pendingEdits[tx.id];
                    const pendingCls = pending ? pending.cls : cls;
                    const pendingCat = pending !== undefined ? pending.cat : (tx.category ?? '');
                    const pendingIsRefund = EXPENSE_REFUND_CLASS_KEYS.has(pendingCls);
                    const dirty = !!pending && (
                      pendingCls !== cls || pendingCat !== (tx.category ?? '')
                    );
                    const clsConfig = BIZ_CLASS[pendingCls] || BIZ_CLASS.unclassified_debit;
                    const isExpanded = !linkMode && expandedTx === tx.id;
                    const isLinkSelected = linkMode && linkSelected.some(s => s.id === tx.id);
                    const amount = parseFloat(tx.convertedTotal ?? tx.total ?? tx.amount ?? 0);
                    const wasConverted = tx.convertedTotal != null && tx.currency && tx.currency !== defaultCurrency;
                    const isCredit = tx.transactionType === 'credit' || tx.transactionDirection === 'credit' || cls === 'business_income' || EXPENSE_REFUND_CLASS_KEYS.has(cls);
                    const sources = tx._sourceReports ?? [{ name: tx._reportName, type: tx._reportType, id: tx.id }];

                    // Link-mode candidate highlighting
                    const selItem = linkMode && linkSelected.length === 1 ? linkSelected[0] : null;
                    const candidateScore = selItem && !isLinkSelected ? (linkCandidates.get(tx.id) ?? 0) : 0;
                    const isDimmed = selItem && !isLinkSelected && candidateScore === 0 && (
                      tx._reportName === selItem._reportName ||
                      (tx._reportType === 'expense') === (selItem._reportType === 'expense')
                    );

                    const handleCardClick = linkMode
                      ? () => {
                        if (isDimmed) return; // incompatible type, ignore click
                        setLinkSelected(prev => {
                          if (prev.some(s => s.id === tx.id)) return prev.filter(s => s.id !== tx.id);
                          if (prev.length >= 2) return prev;
                          return [...prev, tx];
                        });
                      }
                      : () => setExpandedTx(isExpanded ? null : tx.id);

                    const cardBg = isLinkSelected ? '#EFF6FF'
                      : candidateScore === 3 ? '#FFFBEB'
                        : candidateScore >= 1 ? '#FFFEF5'
                          : clsConfig.bg;
                    // : '#fff';
                    const cardBorder = isLinkSelected ? '2px solid #3B82F6'
                      : candidateScore === 3 ? '2px solid #F59E0B'
                        : candidateScore === 2 ? '1.5px solid #FCD34D'
                          : `1.5px solid ${clsConfig.color}`;

                    // : `1.5px solid ${isExpanded ? T.tealDeep : '#E5E7EB'}`;

                    return (
                      <div
                        key={sources.map(s => s.name).join('+') + ':' + tx.id}
                        style={{
                          background: cardBg, borderRadius: 10,
                          border: cardBorder, overflow: 'hidden',
                          opacity: isDimmed ? 0.3 : 1,
                          transition: 'opacity 0.15s, border 0.15s',
                          cursor: isDimmed ? 'default' : undefined,
                        }}
                      >
                        <div
                          onClick={handleCardClick}
                          style={{ padding: '10px 12px', display: 'flex', alignItems: 'center', gap: 8, cursor: 'pointer' }}
                        >
                          {linkMode ? (
                            <div style={{
                              width: 16, height: 16, borderRadius: 4, flexShrink: 0,
                              border: `2px solid ${isLinkSelected ? '#3B82F6' : '#D1D5DB'}`,
                              background: isLinkSelected ? '#3B82F6' : '#fff',
                              display: 'flex', alignItems: 'center', justifyContent: 'center',
                            }}>
                              {isLinkSelected && <span style={{ color: '#fff', fontSize: 10, fontWeight: 900 }}>✓</span>}
                            </div>
                          ) : (
                            <div style={{ width: 10, height: 20, borderRadius: '50%', border: `1px solid ${clsConfig.color}`, flexShrink: 0, background: clsConfig.color }} />
                          )}
                          <div style={{ flex: 1, minWidth: 0 }}>
                            <div style={{ fontSize: 13, fontWeight: 600, whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>
                              {sources.length > 1 ? (() => {
                                const vendors = sources.map(s => s.vendor).filter(Boolean);
                                const unique = [...new Set(vendors)];
                                // if (unique.length < 2) return null;
                                return unique.join(' · ');
                              })() : tx.vendor}
                              {/* {sources.map(s => s.vendor).filter(Boolean)}
                        {sources.map(s => s.vendor).join(' & ')} */}

                            </div>
                            <div style={{ fontSize: 11, color: '#616265', display: 'flex', alignItems: 'center', gap: 4, flexWrap: 'wrap', marginTop: 2 }}>
                              <span>{tx.date}</span>
                              {sources.map(sr => {
                                const pairedSr = sources.find(s => s.name !== sr.name);
                                return (
                                  <button
                                    key={sr.name}
                                    onClick={(e) => {
                                      e.stopPropagation();
                                      const clickedFull = allLoaded[sr.name]?.find(r => r.id === sr.id) ?? tx;
                                      const pairedFull = pairedSr
                                        ? allLoaded[pairedSr.name]?.find(r => r.id === pairedSr.id)
                                        : null;
                                      const merged = {
                                        ...clickedFull,
                                        ...(pairedFull ? {
                                          businessClassification: clickedFull.businessClassification ?? pairedFull.businessClassification,
                                          ...(!clickedFull.imagePreview && pairedFull.imagePreview ? {
                                            imagePreview: pairedFull.imagePreview,
                                            mimeType: pairedFull.mimeType,
                                            imageId: pairedFull.imageId,
                                          } : {}),
                                        } : {}),
                                        _reportName: sr.name,
                                        _reportType: sr.type,
                                      };
                                      setEditingTx(merged);
                                    }}
                                    style={{
                                      background: '#F3F4F6', borderRadius: 4, padding: '1px 5px',
                                      fontSize: 10, fontWeight: 600, color: '#0554f1',
                                      border: 'none', cursor: 'pointer',
                                    }}
                                    onMouseEnter={e => { e.currentTarget.style.background = T.tealPale; e.currentTarget.style.color = T.tealDeep; }}
                                    onMouseLeave={e => { e.currentTarget.style.background = '#F3F4F6'; e.currentTarget.style.color = '#0554f1'; }}
                                    // onMouseLeave={e => { e.currentTarget.style.background = '#F3F4F6'; e.currentTarget.style.color = '#6B7280'; }}
                                    // 
                                    title={`Edit ${sr.name}`}
                                  >
                                    {REPORT_ICONS[sr.type] ?? '🧾'} {sr.name} <Edit size={10} style={{ marginLeft: 3 }} />
                                  </button>
                                );
                              })}
                              {tx.pairedWithFile && (
                                <span style={{
                                  background: T.tealPale, color: T.tealDeep,
                                  borderRadius: 4, padding: '1px 5px',
                                  fontSize: 10, fontWeight: 700,
                                }}> <Link2 size={10} style={{ marginRight: 3 }} />
                                  {tx.pairedWithFile}
                                </span>
                              )}
                              {tx._isLinked && (
                                <span style={{
                                  background: T.tealPale, color: T.tealDeep,
                                  borderRadius: 4, padding: '1px 5px',
                                  fontSize: 10, fontWeight: 700,
                                }}>
                                  linked
                                </span>
                              )}
                              {tx._isDuplicate && !tx._isLinked && (
                                <span style={{
                                  background: '#FEF3C7', color: '#92400e',
                                  borderRadius: 4, padding: '1px 5px',
                                  fontSize: 10, fontWeight: 700,
                                }}>
                                  duplicate
                                </span>
                              )}
                              {tx.reimbursed && (
                                <span title="Reimbursed" style={{
                                  background: '#DCFCE7', color: '#15803d',
                                  borderRadius: 4, padding: '1px 5px',
                                  fontSize: 10, fontWeight: 700,
                                }}>
                                  reimbursed
                                </span>
                              )}
                              {candidateScore === 3 && (
                                <span style={{ background: '#F59E0B', color: '#fff', borderRadius: 4, padding: '1px 5px', fontSize: 9, fontWeight: 800 }}>
                                  ★ Best match
                                </span>
                              )}
                              {candidateScore === 2 && (
                                <span style={{ background: '#FEF3C7', color: '#92400e', borderRadius: 4, padding: '1px 5px', fontSize: 9, fontWeight: 700 }}>
                                  Possible match
                                </span>
                              )}
                            </div>
                          </div>
                          <div style={{ display: 'flex', alignItems: 'center', gap: 6, flexShrink: 0 }}>
                            <div style={{ textAlign: 'right' }}>
                              <span style={{ fontSize: 13, fontWeight: 700, color: isCredit ? '#15803d' : '#111', ...amountStyle() }}>
                                {isCredit ? '+' : ''}{formatAmount(amount, defaultCurrency)}
                              </span>
                              {wasConverted && (
                                <div style={{ fontSize: 10, color: '#9CA3AF' }}>
                                  {tx.currency} {parseFloat(tx.total).toFixed(2)}
                                </div>
                              )}
                            </div>
                            {!linkMode && <ChevronDown size={14} color="#9CA3AF" style={{ transform: isExpanded ? 'rotate(180deg)' : 'none', transition: 'transform 0.15s' }} />}
                          </div>
                        </div>

                        {isExpanded && (
                          <div style={{ padding: '8px 12px 12px', borderTop: '1px solid #F3F4F6', background: '#FAFAFA' }}>
                            {/* Badge + category */}
                            <div style={{ display: 'flex', alignItems: 'center', gap: 6, flexWrap: 'wrap', marginBottom: 8 }}>
                              <ClassBadge cls={pendingCls} />
                              <span style={{ fontSize: 11, color: '#9CA3AF' }}>
                                {categoryLabel(pendingCat || tx.category)}{tx.transactionType ? ` · ${tx.transactionType}` : ''}
                              </span>
                            </div>
                            {/* Classification */}
                            <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                              <span style={{ fontSize: 12, color: '#374151', fontWeight: 600 }}>Classify:</span>
                              <ClassSelect
                                value={pendingCls}
                                onChange={(newCls) => {
                                  const wasIncome = INCOME_CLS.has(pendingCls);
                                  const nowIncome = INCOME_CLS.has(newCls);
                                  const nowRefund = EXPENSE_REFUND_CLASS_KEYS.has(newCls);
                                  setPendingEdits(prev => ({
                                    ...prev,
                                    [tx.id]: {
                                      cls: newCls,
                                      // Refund classifications force a category re-pick. Income↔expense switches also reset.
                                      cat: nowRefund || wasIncome !== nowIncome ? '' : pendingCat,
                                    },
                                  }));
                                }}
                              />
                            </div>
                            {/* Refund hint — shown when a refund classification is picked */}
                            {pendingIsRefund && (
                              <div style={{ fontSize: 11, color: '#15803d', marginTop: 6 }}>
                                Pick the expense category this refunded.
                              </div>
                            )}
                            {/* Category — income categories for income; expense categories for everything else (including refunds) */}
                            {(INCOME_CLS.has(pendingCls) || EXPENSE_CLS.has(pendingCls)) && (
                              <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginTop: 6 }}>
                                <span style={{ fontSize: 12, color: '#374151', fontWeight: 600 }}>Category:</span>
                                <CategorySelect
                                  value={pendingCat}
                                  onChange={(cat) => setPendingEdits(prev => ({
                                    ...prev,
                                    [tx.id]: { cls: pendingCls, cat },
                                  }))}
                                  categories={INCOME_CLS.has(pendingCls) ? INCOME_CATEGORIES : categories}
                                  isIncome={INCOME_CLS.has(pendingCls)}
                                />
                              </div>
                            )}
                            {/* Save Changes */}
                            {saving[tx.id] ? (
                              <div style={{ fontSize: 11, color: '#6B7280', marginTop: 8 }}>Saving…</div>
                            ) : dirty && (
                              <div style={{ marginTop: 10 }}>
                                <button
                                  onClick={(e) => { e.stopPropagation(); saveItemChanges(tx, { cls: pendingCls, cat: pendingCat }); }}
                                  style={{
                                    padding: '6px 16px', borderRadius: 7, border: 'none',
                                    background: T.gradTeal, color: '#fff',
                                    fontSize: 12, fontWeight: 700, cursor: 'pointer',
                                  }}
                                >
                                  Save Changes
                                </button>
                              </div>
                            )}
                            {tx._isLinked && (
                              <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginTop: 6 }}>
                                <span style={{ fontSize: 11, color: T.tealDeep }}>
                                  🔗 Linked across {sources.length} reports — classification saved to all
                                </span>
                                <button
                                  onClick={(e) => { e.stopPropagation(); unlinkPair(tx); }}
                                  style={{ fontSize: 11, fontWeight: 700, padding: '2px 8px', borderRadius: 5, border: '1.5px solid #FECACA', background: '#FEF2F2', color: '#dc2626', cursor: 'pointer' }}
                                >
                                  Unlink
                                </button>
                              </div>
                            )}
                            {tx._isDuplicate && !tx._isLinked && (
                              <div style={{ fontSize: 11, color: '#92400e', marginTop: 6 }}>
                                ⚠ Appears in {sources.length} reports — classification saved to all
                              </div>
                            )}
                            {tx.transferPairId && (
                              <div style={{ fontSize: 11, color: '#9333ea', marginTop: 6 }}>Matched transfer pair</div>
                            )}
                            {tx.notes && <div style={{ fontSize: 12, color: '#6B7280', marginTop: 4 }}>{tx.notes}</div>}
                            {tx._reportType === 'expense' && !tx._isLinked && (
                              <div style={{ display: 'flex', justifyContent: 'flex-end', marginTop: 8 }}>
                                <button
                                  onClick={(e) => { e.stopPropagation(); if (window.confirm(`Delete "${tx.vendor}" from ${tx._reportName}?`)) handleDeleteExpense(tx); }}
                                  style={{ display: 'flex', alignItems: 'center', gap: 4, fontSize: 11, fontWeight: 700, padding: '3px 9px', borderRadius: 5, border: '1.5px solid #FECACA', background: '#FEF2F2', color: '#dc2626', cursor: 'pointer' }}
                                >
                                  <Trash2 size={11} /> Delete
                                </button>
                              </div>
                            )}
                          </div>
                        )}
                      </div>
                    );
                  })}
                </div>
              );
            })}
          </div>
        )}

        {!loading && currentSelected.length === 0 && allFilesSorted.length > 0 && (
          <div style={{ textAlign: 'center', padding: 60, color: '#9CA3AF' }}>
            <div style={{ fontSize: 36, marginBottom: 10 }}>📊</div>
            <div style={{ fontSize: 15, fontWeight: 700, marginBottom: 6, color: '#374151' }}>Select reports above to get started</div>
            <div style={{ fontSize: 13 }}>Tap any report pill to include it in the summary.</div>
          </div>
        )}

        {!loading && allFilesSorted.length === 0 && (
          <div style={{ padding: '32px 20px 60px', display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 24 }}>
            <div style={{ textAlign: 'center' }}>
              <div style={{ fontSize: 40, marginBottom: 10 }}>📊</div>
              <div style={{ fontSize: 17, fontWeight: 900, color: '#111', marginBottom: 8 }}>Your financial picture starts here</div>
              <div style={{ fontSize: 14, color: '#6B7280', lineHeight: 1.6, maxWidth: 320 }}>
                Import bank or credit card statements, or add expense reports with scanned receipts —
                then classify every dollar as business or personal to see your real-time P&amp;L.
              </div>
            </div>

            <div style={{ display: 'flex', flexDirection: 'column', gap: 10, width: '100%', maxWidth: 340 }}>
              {onImportCreditCard && (
                <button
                  onClick={onImportCreditCard}
                  style={{
                    display: 'flex', alignItems: 'center', gap: 12,
                    padding: '14px 16px', borderRadius: 12, cursor: 'pointer',
                    border: `1.5px solid ${T.tealDeep}`, background: T.tealPale,
                    textAlign: 'left',
                  }}
                >
                  <div style={{ fontSize: 24 }}>💳</div>
                  <div>
                    <div style={{ fontSize: 14, fontWeight: 800, color: T.tealDeep }}>Import Credit Card CSV</div>
                    <div style={{ fontSize: 12, color: '#6B7280', marginTop: 2 }}>Download from your card issuer and upload here</div>
                  </div>
                </button>
              )}
              {onImportBank && (
                <button
                  onClick={onImportBank}
                  style={{
                    display: 'flex', alignItems: 'center', gap: 12,
                    padding: '14px 16px', borderRadius: 12, cursor: 'pointer',
                    border: '1.5px solid #0369a1', background: '#EFF6FF',
                    textAlign: 'left',
                  }}
                >
                  <div style={{ fontSize: 24 }}>🏦</div>
                  <div>
                    <div style={{ fontSize: 14, fontWeight: 800, color: '#0369a1' }}>Import Bank Statement CSV</div>
                    <div style={{ fontSize: 12, color: '#6B7280', marginTop: 2 }}>Supports Chase, Bank of America, Wells Fargo, and more</div>
                  </div>
                </button>
              )}
              {onGoToExpenses && (
                <button
                  onClick={onGoToExpenses}
                  style={{
                    display: 'flex', alignItems: 'center', gap: 12,
                    padding: '14px 16px', borderRadius: 12, cursor: 'pointer',
                    border: '1.5px solid #6B7280', background: '#F9FAFB',
                    textAlign: 'left',
                  }}
                >
                  <div style={{ fontSize: 24 }}>🧾</div>
                  <div>
                    <div style={{ fontSize: 14, fontWeight: 800, color: '#374151' }}>Scan Receipts into Expense Reports</div>
                    <div style={{ fontSize: 12, color: '#6B7280', marginTop: 2 }}>Use the Add or Capture tab to build expense reports</div>
                  </div>
                </button>
              )}
            </div>

            <button
              onClick={() => setShowHelp(true)}
              style={{
                display: 'flex', alignItems: 'center', gap: 6,
                padding: '8px 16px', borderRadius: 20, cursor: 'pointer',
                border: `1.5px solid ${T.tealDeep}`, background: 'transparent',
                fontSize: 13, fontWeight: 700, color: T.tealDeep,
              }}
            >
              <HelpCircle size={14} /> How does this work?
            </button>
          </div>
        )}
      </div>

      {/* ── Bulk reclassification offer bar ─────────────────────── */}
      {bulkOffer && (
        <div style={{
          position: 'absolute', bottom: 0, left: 0, right: 0,
          background: T.navy, color: '#fff',
          padding: '12px 16px',
          display: 'flex', alignItems: 'center', gap: 10,
          boxShadow: '0 -4px 20px rgba(0,0,0,0.25)',
          zIndex: 10,
        }}>
          <div style={{ flex: 1, minWidth: 0 }}>
            <div style={{ fontSize: 13, fontWeight: 700, whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>
              {bulkOffer.matchIds.length} other <em>{bulkOffer.vendor}</em> transaction{bulkOffer.matchIds.length !== 1 ? 's' : ''} in this report
            </div>
            <div style={{ fontSize: 12, color: 'rgba(255,255,255,0.65)', marginTop: 2 }}>
              Apply{' '}
              <strong style={{ color: '#fff' }}>{BIZ_CLASS[bulkOffer.newClass]?.label}</strong>
              {bulkOffer.newCategory != null && (
                <> · <strong style={{ color: '#fff' }}>{categoryLabel(bulkOffer.newCategory)}</strong></>
              )}
              {' '}to all?
            </div>
          </div>
          <button
            onClick={applyBulkOffer}
            disabled={bulkSaving}
            style={{
              padding: '8px 14px', borderRadius: 8, border: 'none',
              background: T.gradTeal, color: '#fff',
              fontSize: 13, fontWeight: 700, cursor: bulkSaving ? 'wait' : 'pointer',
              whiteSpace: 'nowrap', flexShrink: 0,
            }}
          >
            {bulkSaving ? 'Applying…' : 'Apply to all'}
          </button>
          <button
            onClick={() => setBulkOffer(null)}
            style={{
              padding: '8px 10px', borderRadius: 8, border: '1.5px solid rgba(255,255,255,0.25)',
              background: 'transparent', color: 'rgba(255,255,255,0.7)',
              fontSize: 13, fontWeight: 600, cursor: 'pointer', flexShrink: 0,
            }}
          >
            Dismiss
          </button>
        </div>
      )}

      {/* ── Link confirmation bar ───────────────────────────────── */}
      {linkMode && linkSelected.length > 0 && (() => {
        const expenseTx = linkSelected.find(t => t._reportType === 'expense');
        const otherTx = linkSelected.find(t => t._reportType !== 'expense');
        const ready = linkSelected.length === 2 && expenseTx && otherTx;
        const invalid = linkSelected.length === 2 && !ready;

        const expAmt = expenseTx ? parseFloat(expenseTx.convertedTotal ?? expenseTx.total ?? 0) : 0;
        const otherAmt = otherTx ? parseFloat(otherTx.convertedTotal ?? otherTx.total ?? otherTx.amount ?? 0) : 0;
        const diff = ready ? Math.abs(otherAmt - expAmt) : 0;

        return (
          <div style={{
            position: 'absolute', bottom: 0, left: 0, right: 0,
            background: invalid ? '#7C3AED' : '#1E3A5F',
            color: '#fff', padding: '12px 16px',
            display: 'flex', alignItems: 'center', gap: 10,
            boxShadow: '0 -4px 20px rgba(0,0,0,0.25)', zIndex: 10,
          }}>
            <div style={{ flex: 1, minWidth: 0 }}>
              {invalid ? (
                <div style={{ fontSize: 13, fontWeight: 700 }}>
                  ⚠ Both items must come from different report types (one expense report + one CC/bank).
                </div>
              ) : ready ? (
                <>
                  <div style={{ fontSize: 13, fontWeight: 700, marginBottom: 2 }}>
                    Link <em>{expenseTx.vendor}</em> ({REPORT_ICONS.expense} {expenseTx._reportName}) ↔ <em>{otherTx.vendor}</em> ({REPORT_ICONS[otherTx._reportType] ?? '🧾'} {otherTx._reportName})
                  </div>
                  <div style={{ fontSize: 12, color: 'rgba(255,255,255,0.7)' }}>
                    {diff > 0.005
                      ? `Amount differs by ${formatAmount(diff, defaultCurrency)} — expense report will be updated to ${formatAmount(otherAmt, defaultCurrency)}`
                      : 'Amounts match — will mark as linked'}
                  </div>
                </>
              ) : (
                <div style={{ fontSize: 13, fontWeight: 700 }}>
                  {linkSelected.length === 1
                    ? `Selected: ${linkSelected[0].vendor} — now select one more item from a different report type`
                    : 'Select 2 items to link'}
                </div>
              )}
            </div>
            {ready && (
              <button
                onClick={confirmLink}
                disabled={linkSaving}
                style={{ padding: '8px 14px', borderRadius: 8, border: 'none', background: T.gradTeal, color: '#fff', fontSize: 13, fontWeight: 700, cursor: linkSaving ? 'wait' : 'pointer', whiteSpace: 'nowrap', flexShrink: 0 }}
              >
                {linkSaving ? 'Linking…' : 'Link & Reconcile'}
              </button>
            )}
            <button
              onClick={() => { setLinkSelected([]); setLinkMode(false); }}
              style={{ padding: '8px 10px', borderRadius: 8, border: '1.5px solid rgba(255,255,255,0.25)', background: 'transparent', color: 'rgba(255,255,255,0.7)', fontSize: 13, fontWeight: 600, cursor: 'pointer', flexShrink: 0 }}
            >
              Cancel
            </button>
          </div>
        );
      })()}

      {/* ── Edit transaction modal ───────────────────────────────── */}
      {editingTx && (
        <EditModal
          expense={editingTx}
          onSave={handleEditSave}
          onClose={() => setEditingTx(null)}
          categories={categories}
          incomeCategories={incomeCategories}
          addCategory={addCategory}
          addIncomeCategory={addIncomeCategory}
          savedFiles={savedFiles}
          currentFileName={editingTx._reportName}
          onCopyExpense={editingTx._reportType === 'expense' ? handleCopyExpense : null}
          onMoveExpense={editingTx._reportType === 'expense' ? handleMoveExpense : null}
        />
      )}
      {showHelp && (
        <BSVHelpModal
          onClose={() => setShowHelp(false)}
          onImportCreditCard={onImportCreditCard}
          onImportBank={onImportBank}
          onGoToExpenses={onGoToExpenses}
        />
      )}
      {showReconcile && (
        <ReconcileModal
          allLoaded={allLoaded}
          savedFiles={savedFiles}
          uid={uid}
          defaultCurrency={defaultCurrency}
          canAiReconcile={canAiReconcile}
          onLink={handleReconcileLink}
          onClassify={handleClassifyOne}
          onClose={() => setShowReconcile(false)}
        />
      )}

      {onGoToCapture && (
        <button
          onClick={onGoToCapture}
          title="Scan Receipt"
          style={{
            position: 'absolute', bottom: 24, right: 24, zIndex: 10,
            width: 36, height: 36, borderRadius: '50%', border: 'none',
            background: T.gradTeal, color: '#fff', cursor: 'pointer',
            display: 'flex', alignItems: 'center', justifyContent: 'center',
            boxShadow: '0 4px 16px rgba(0,0,0,0.25)',
          }}
        >
          <Camera size={24} />
        </button>
      )}
    </div>
  );
}
